import type { Statement } from '@xapi/xapi'
import lodash from 'lodash'

import type { ILmsEvent, IProviderConfig, LmsType } from '../types'
import { XapiProvider, type IXapiProviderOptions } from './xapi.provider'

export interface ICmi5ProviderOptions extends IXapiProviderOptions {
  fetchUrl?: string
}

export class Cmi5Provider extends XapiProvider {
  readonly type: LmsType = 'cmi5'

  private sessionId?: string
  private cmi5Options: ICmi5ProviderOptions

  constructor(options: ICmi5ProviderOptions = {}) {
    super(options)
    this.cmi5Options = options
  }

  async initialize(config: IProviderConfig): Promise<boolean> {
    // cmi5 requires fetching auth token from fetch URL
    const fetchUrl = this.cmi5Options.fetchUrl
    if (fetchUrl) {
      const authToken = await this.fetchCmi5AuthToken(fetchUrl)
      if (authToken) {
        config = { ...config, auth: authToken }
      }

      // Extract session ID from fetch URL
      const sessionMatch = fetchUrl.match(/[?&]session=([^&]+)/)
      if (sessionMatch) {
        this.sessionId = sessionMatch[1]
      }
    }

    const result = await super.initialize(config)
    if (result) {
      this.setLearnerInfo({ ...this.learnerInfo, lmsType: 'cmi5', sessionId: this.sessionId })
    }

    return result
  }

  sendAnalyticsViaBeacon(_event: ILmsEvent[]): boolean {
    return false
  }

  private async fetchCmi5AuthToken(fetchUrl: string): Promise<string | null> {
    try {
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        console.warn('[Cmi5Provider] fetch failed:', response.status)
        return null
      }

      const data = await response.json()

      // cmi5 spec: response is { "auth-token": "..." }
      if (data['auth-token']) {
        return data['auth-token']
      }

      // Some LMS return just the token string
      if (typeof data === 'string') {
        return data
      }

      console.warn('[Cmi5Provider] fetch: unexpected response format')
      return null
    } catch (error) {
      console.error('[Cmi5Provider] fetch error:', error)
      return null
    }
  }

  async trackEvent(event: ILmsEvent): Promise<void> {
    if (!this.lrs || !this.statementBuilder) {
      return
    }

    // Build statement and add cmi5 session extension
    const isLocal = Boolean(this.options.useLocalStatements)
    const statement = this.statementBuilder.buildStatement(event, isLocal)

    // Add cmi5 session ID to context
    const cmi5Statement = this.addCmi5Context(statement)

    this.queueStatement(cmi5Statement)
  }

  private addCmi5Context(statement: Statement): Statement {
    if (!this.sessionId) {
      return statement
    }

    return lodash.merge({}, statement, {
      context: {
        extensions: {
          'https://w3id.org/xapi/cmi5/context/extensions/sessionid': this.sessionId,
        },
      },
    })
  }
}
